import styled from 'styled-components'
import PropTypes from 'prop-types'
import SwitchComponent from '@mui/material/Switch'

import { base } from '../../mixins'
import { PB40T, PB90T } from '../../colors'

const displays = {
  default: 'primary',
  blue: 'secondary',
  red: 'error',
  monochrome: 'default'
}
const textSizes = {
  small: '12px',
  medium: '14px'
}
const getLabelMargin = ({ labelPosition, isFirstLabel }) => {
  if (labelPosition === 'both') {
    if (isFirstLabel) {
      return 'margin-right: 3px'
    }

    return 'margin-left: 3px'
  }

  if (labelPosition === 'left') {
    return 'margin-right: 3px'
  }

  return 'margin-left: 3px'
}
const getOrder = ({ labelPosition }) => {
  if (/both|left/.test(labelPosition)) {
    return 0
  }

  return 2
}
const getOffColor = ({ $offColor }) => {
  if (!$offColor) {
    return `
      color: ${PB40T};

      & + .MuiSwitch-track {
        background-color: ${PB90T};
        opacity: 0.8;
      }
    `
  }

  return `
    color: ${$offColor};

    & + .MuiSwitch-track {
      background-color: ${$offColor};
      opacity: 0.5;
    }
  `
}
const Label = styled.label`
  display: inline-flex;
  align-items: center;
  text-align: center;
`
const Text = styled.span`
  ${base};
  ${getLabelMargin};
  font-size: ${({ size }) => textSizes[size]};
  font-weight: ${({ weight }) => weight ?? 700};
  color: ${({ color }) => color};
  cursor: ${({ isDisabled }) => (isDisabled ? 'not-allowed' : 'default')};
  order: ${getOrder};
`
const StyledSwitch = styled(SwitchComponent)`
  &.MuiSwitch-root:not(.MuiSwitch-sizeSmall) {
    .MuiSwitch-switchBase {
      &:not(.Mui-checked) {
        ${getOffColor};
      }
    }
  }
`
/**
 * The Switch is used to toggle between different states.
 */
const Switch = ({
  isOn,
  label,
  offColor,
  onChange = () => {},
  labelPosition = 'left',
  color = 'black',
  size = 'medium',
  display = 'default',
  defaultOn = false,
  isDisabled = false,
  ...rest
}) => {
  let labels = label
  let positioning = 'both'
  const props = {
    size,
    onChange,
    $offColor: offColor,
    disabled: isDisabled,
    color: displays[display]
  }

  // Decide between controlled vs uncontrolled usage
  if (typeof isOn !== 'undefined') {
    props.checked = isOn
  } else {
    props.defaultChecked = defaultOn
  }

  if (!Array.isArray(label)) {
    labels = [label]
    positioning = labelPosition
  }

  return (
    <Label>
      {labels[0] && (
        <Text
          isFirstLabel
          weight={isOn ? 300 : 700}
          color={color}
          size={size}
          labelPosition={positioning}
          isDisabled={isDisabled}>
          {labels[0]}
        </Text>
      )}
      <StyledSwitch {...props} {...rest} />
      {labels[1] && (
        <Text
          weight={isOn ? 700 : 300}
          color={color}
          size={size}
          labelPosition={positioning}
          isDisabled={isDisabled}>
          {labels[1]}
        </Text>
      )}
    </Label>
  )
}

Switch.propTypes = {
  /** How the switch is colored */
  display: PropTypes.oneOf(['default', 'blue', 'red', 'monochrome']),
  /** The size of the switch */
  size: PropTypes.oneOf(['small', 'medium']),
  /** Whether the switch is on or off initially */
  defaultOn: PropTypes.bool,
  /** Whether the switch is on or off */
  isOn: PropTypes.bool,
  /** Whether the switch is disabled */
  isDisabled: PropTypes.bool,
  /** Custom color for the off state (overrides the `display` if both set) */
  offColor: PropTypes.string,
  /** Sets the label color */
  color: PropTypes.string,
  /** Callback when the switch state changes */
  onChange: PropTypes.func,
  /** The label for the switch */
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.array]).isRequired,
  /** What side of the switch the label text is on */
  labelPosition: PropTypes.oneOf(['left', 'right', 'both'])
}

export { Switch }
